import { z } from "zod/v4"
import { CreatedAt, UpdatedAt } from "./system"

/**
 * GeoIP CIDRオーバーライドのアクション
 * include = 追加（自動更新データに含まれない範囲を補完）
 * exclude = 除外（自動更新データに含まれる範囲を除外）
 */
export const GeoIpCidrOverrideAction = z.enum(["include", "exclude"])

export type GeoIpCidrOverrideAction = z.infer<typeof GeoIpCidrOverrideAction>

/**
 * GeoIP CIDRオーバーライドエントリ（手動追加/除外）
 *
 * 管理画面から手動で追加/除外するCIDR範囲。
 * app-configs/geo-ip-cidr ドキュメント内の overrides 配列に格納される。
 */
export const GeoIpCidrOverrideEntry = z.object({
  cidr: z.string().min(1).describe("CIDR表記（例: 1.0.16.0/20, 2001:200::/32）"),
  ip_version: z.enum(["v4", "v6"]).describe("IPバージョン"),
  action: GeoIpCidrOverrideAction.describe("include=追加, exclude=除外"),
  note: z.string().describe("変更理由のメモ"),
  created_at: z.number().describe("作成日時（Unix timestamp ms）"),
})

export type GeoIpCidrOverrideEntry = z.infer<typeof GeoIpCidrOverrideEntry>

/**
 * GeoIP CIDRデータ（app-configs/geo-ip-cidr ドキュメント）
 *
 * ipdenyから取得したCIDRデータ（自動更新）と手動オーバーライドを
 * 単一ドキュメントに格納する。
 *
 * 判定ロジック: (ipv4_cidrs + include overrides) - exclude overrides
 */
export const AppConfigGeoIpCidrDocumentMeta = z.object({
  country_code: z.string().regex(/^[A-Z]{2}$/).describe("ISO 3166-1 alpha-2形式の国コード"),
  version: z.string().describe("自動更新データのバージョン（例: 2026-01-05）"),
  ipv4_cidrs: z.array(z.string()).describe("自動更新されたIPv4 CIDRリスト"),
  ipv6_cidrs: z.array(z.string()).describe("自動更新されたIPv6 CIDRリスト"),
  overrides: z.array(GeoIpCidrOverrideEntry).describe("手動追加/除外のオーバーライド一覧"),
})

export type AppConfigGeoIpCidrDocumentMeta = z.infer<typeof AppConfigGeoIpCidrDocumentMeta>

export const AppConfigGeoIpCidrDocumentBody = AppConfigGeoIpCidrDocumentMeta.extend({
  created_at: CreatedAt,
  updated_at: UpdatedAt,
})

export type AppConfigGeoIpCidrDocumentBody = z.infer<typeof AppConfigGeoIpCidrDocumentBody>

/**
 * オーバーライド追加リクエスト（管理API用）
 */
export const CreateGeoIpCidrOverrideRequest = z.object({
  cidr: z.string().min(1),
  ipVersion: z.enum(["v4", "v6"]),
  action: GeoIpCidrOverrideAction,
  note: z.string(),
})

export type CreateGeoIpCidrOverrideRequest = z.infer<typeof CreateGeoIpCidrOverrideRequest>

/**
 * オーバーライド削除リクエスト（管理API用）
 *
 * 削除対象を一意に特定するために cidr + ip_version + action の組み合わせを使う
 */
export const DeleteGeoIpCidrOverrideRequest = z.object({
  cidr: z.string().min(1),
  ipVersion: z.enum(["v4", "v6"]),
  action: GeoIpCidrOverrideAction,
})

export type DeleteGeoIpCidrOverrideRequest = z.infer<typeof DeleteGeoIpCidrOverrideRequest>
